import { useQuery } from '@tanstack/react-query'
import { graphql } from '@/lib/api'
import { DOC_TREES } from '@/lib/github-queries'
import { appConfig, keys } from '@/lib/query'
import { docTitleFromFile, slugFromFile } from '@/lib/docs'

export type WorkDoc = {
  slug: string
  file: string
  title: string
  /** Sidebar lists README + the role guides; root docs serve cross-links only. */
  inSidebar: boolean
}

type TreeEntries = { entries?: Array<{ name: string; type: string }> } | null

const FALLBACK: WorkDoc[] = [
  { slug: 'readme', file: 'README.md', title: 'README', inSidebar: true },
]

/** Docs discovered live from the work repo — new guides appear automatically. */
export function useWorkDocs(): WorkDoc[] {
  const { data } = useQuery({
    queryKey: keys.workDocs,
    staleTime: 60 * 60 * 1000,
    placeholderData: undefined,
    queryFn: async (): Promise<WorkDoc[]> => {
      const res = await graphql<{ repository: { root: TreeEntries; guides: TreeEntries } }>({
        query: DOC_TREES,
        variables: { owner: appConfig.owner, name: appConfig.repo },
      })
      const docs: WorkDoc[] = []
      for (const entry of res.repository.root?.entries ?? []) {
        if (entry.type === 'blob' && entry.name.endsWith('.md')) {
          docs.push({
            slug: slugFromFile(entry.name),
            file: entry.name,
            title: docTitleFromFile(entry.name),
            inSidebar: entry.name === 'README.md',
          })
        }
      }
      for (const entry of res.repository.guides?.entries ?? []) {
        if (entry.type === 'blob' && entry.name.endsWith('.md')) {
          docs.push({
            slug: slugFromFile(`guides/${entry.name}`),
            file: `guides/${entry.name}`,
            title: docTitleFromFile(entry.name),
            inSidebar: true,
          })
        }
      }
      // README first, then guides alphabetically, then the rest.
      return docs.sort(
        (a, b) =>
          Number(b.inSidebar) - Number(a.inSidebar) ||
          Number(b.file === 'README.md') - Number(a.file === 'README.md') ||
          a.title.localeCompare(b.title),
      )
    },
  })
  return data ?? FALLBACK
}
